import { FunctionComponent, memo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OrganisationTag } from '../../../../services';
import { getCssVar, layoutPath } from '../../../utils';
import { IconLabel } from '../../IconLabel';

import classNames from 'classnames';
import classes from './FeedCardTags.module.scss';

export enum FeedCardTagsVariant {
  TEXT = 'TEXT',
  BADGE = 'BADGE',
}

interface RenderTagArgs {
  id: number;
  name: string;
  path: string;
  isActive: boolean;
}

interface FeedCardTagsProps {
  tags: OrganisationTag[];
  className?: string;
  variant?: FeedCardTagsVariant;
}

export const FeedCardTags: FunctionComponent<FeedCardTagsProps> = memo(
  ({ tags, className, variant = FeedCardTagsVariant.TEXT }) => {
    const navigate = useNavigate();

    const { pathname } = useLocation();

    const renderBadge = useCallback(
      ({ id, name, path, isActive }: RenderTagArgs) => {
        const Tag = isActive ? 'div' : 'button';

        return (
          <Tag
            key={id}
            className={classNames(classes['tags__badge'], {
              [classes['tags__badge--disabled']]: isActive,
            })}
            onClick={() => !isActive && navigate(path)}
            {...(!isActive && { type: 'button' })}
          >
            {name}
          </Tag>
        );
      },
      [navigate]
    );

    const renderText = useCallback(
      ({ id, name, path, isActive }: RenderTagArgs) => (
        <IconLabel
          key={id}
          label={`#${name}`}
          className={classes['tags__text']}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
          onClick={() => navigate(path)}
          disabled={isActive}
        />
      ),
      [navigate]
    );

    if (!tags.length) {
      return null;
    }

    return (
      <div className={classNames(classes['tags'], className)}>
        {tags.map(({ id, name }) => {
          const path = layoutPath(`/tags/${id}`);

          const isActive = pathname === path;

          switch (variant) {
            case FeedCardTagsVariant.BADGE:
              return renderBadge({ id, name, path, isActive });
            case FeedCardTagsVariant.TEXT:
              return renderText({ id, name, path, isActive });
            default:
              return null;
          }
        })}
      </div>
    );
  }
);
